import React, { useMemo } from "react";
import PropTypes from "prop-types";

import UserService from "modules/user/service/UserService";
import DoctorCategory from "constants/type/user/DoctorCategory";
import SIDEBAR_ICONS from "../helper/sidebarIcons";
import { useSheetCounts } from "./sheetCounts";

/**
 * The patient-screen left nav. Source: Figma "Sidebar · Expanded" (3015:4952),
 * read exactly via Dev Mode MCP. Three groups: Quick access, Medical history,
 * Lifestyle. Lifestyle rows carry a completion ring instead of an icon badge.
 *
 * Icons are the real Figma vectors, rendered as raw SVG so they theme via
 * currentColor. They come from a LATER board than the layout above — "Sidebar ·
 * live reference" (6189:4012) — see helper/sidebarIcons.js.
 *
 * COLLAPSING: the toggle in the top bar folds the panel to a rail holding only
 * that button, so it can always be reopened. The button existed before this and
 * was wired to nothing — `onCollapse` was never passed by PatientShell.
 */

/**
 * The panel toggle, 18x18: Figma components icon/panel-toggle/expanded (359:54,
 * panel open, click to collapse) and icon/panel-toggle/collapsed (359:58, click
 * to expand), from the Dev Mode MCP export (2026-10-06, placed in the head at
 * 3015:4957 and on the rail at 1942:7525). Figma redrew both as a double
 * chevron — « while the panel is open, » while it is folded — replacing the
 * panel outline, divider rule and single chevron that used to be drawn here.
 * Each state is its own component now, and these are its own path strings:
 * two 3.4x6.8 strokes, 1.6 wide, round caps and joins. The colour (#4A5565)
 * comes from .ps-collapse through currentColor.
 */
const PanelToggle = ({ collapsed }) => (
  <svg
    className="ps-toggle-icon"
    viewBox="0 0 18 18"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.6"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    {collapsed ? (
      <>
        <path d="M9.6 12.4L13 9L9.6 5.6" />
        <path d="M5 12.4L8.4 9L5 5.6" />
      </>
    ) : (
      <>
        <path d="M8.4 12.4L5 9L8.4 5.6" />
        <path d="M13 12.4L9.6 9L13 5.6" />
      </>
    )}
  </svg>
);
PanelToggle.propTypes = { collapsed: PropTypes.bool };
const Icon = ({ name }) => (
  <span
    className="ps-icon"
    aria-hidden="true"
    dangerouslySetInnerHTML={{ __html: SIDEBAR_ICONS[name] || "" }}
  />
);
Icon.propTypes = { name: PropTypes.string.isRequired };

/* Hardcoded nav model — matches the Figma frame. `icon` is the key into
   SIDEBAR_ICONS.

   The four Lifestyle rows used to carry a `pct` completion ring. Nothing ever
   computed it — every one was hardcoded 0 — so it read as "0% complete" on
   sections the clinician had actually filled in. Removed rather than wired up:
   there is no completion signal in any of these sections' payloads to derive
   it from. */
const GROUPS = [
  {
    label: "Quick access",
    items: [
      { key: "ai-transcript", name: "ECHO - AI transcript" },
      { key: "patient-details", name: "Patient details" },
      { key: "progress-notes", name: "Progress notes" },
    ],
  },
  {
    label: "Medical history",
    items: [
      { key: "anthropometric", name: "Anthropometric data" },
      { key: "chief-complaints", name: "Chief complaints" },
      { key: "diagnosis", name: "Diagnosis" },
      { key: "medicines", name: "Medicines" },
      { key: "lab-investigations", name: "Lab investigations" },
      { key: "allergies", name: "Allergies" },
      { key: "family-history", name: "Family history" },
      { key: "hospitalisation", name: "Hospitalisation history" },
      { key: "drug-schedule", name: "Drug schedule" },
    ],
  },
  {
    label: "Lifestyle",
    items: [
      { key: "food-preference", name: "Food preferences" },
      { key: "daily-schedule", name: "Daily schedule" },
      { key: "physical-activity", name: "Physical activity" },
      { key: "addictions", name: "Addictions" },
    ],
  },
];

/* Which clinician categories get the "ECHO - AI transcript" row. The AI
   transcript call runs in-browser via LiveKit for these categories; every
   other one (doctor / psychotherapist / ops-only employee) is routed
   through SparkTG by /initiate-call and has no reason to see the row — it
   would just open a modal they can't use. Same category split the backend
   already applies for vendor selection (patient-communication's
   IsEchoEligibleCategory). */
const AI_TRANSCRIPT_CATEGORIES = new Set([
  DoctorCategory.NUTRITIONIST,
  DoctorCategory.MEDICAL_TEAM,
  DoctorCategory.PHYSIOTHERAPIST,
]);

const PatientSidebar = ({ active, onSelect, collapsed, onToggle }) => {
  const groups = useMemo(() => {
    const category = new UserService().doctorCategory();
    if (AI_TRANSCRIPT_CATEGORIES.has(category)) return GROUPS;
    return GROUPS.map((group) =>
      group.label === "Quick access"
        ? { ...group, items: group.items.filter((it) => it.key !== "ai-transcript") }
        : group,
    );
  }, []);

  // Per-section open-editor-cards count (ZY-10350). Read from the shell-
  // level provider; each editor publishes its stack length via
  // useSheetCountRegistration. Zero / absent → no badge rendered.
  const sheetCounts = useSheetCounts();

  return (
  <nav
    className={`patient-sidebar${collapsed ? " is-collapsed" : ""}`}
    aria-label="Patient sections"
  >
    <div className="ps-head">
      {!collapsed && <span className="ps-head-label">Quick access</span>}
      <button
        type="button"
        className="ps-collapse"
        aria-label={collapsed ? "Expand quick access" : "Collapse quick access"}
        aria-expanded={!collapsed}
        title={collapsed ? "Expand quick access" : "Collapse quick access"}
        onClick={onToggle}
      >
        <PanelToggle collapsed={collapsed} />
      </button>
    </div>

    {/* Collapsed = Figma "Sidebar · Collapsed (icons only)" (1944:1843): a 64px
        rail of 40x40 cells. Same rows, same active state, same click targets —
        the label and the completion ring are dropped, the count is not. */}
    {groups.map((group, gi) => (
      <div className="ps-group" key={group.label}>
        {/* The first group's label is rendered in the head, next to the collapse
            control, so skip repeating it. */}
        {gi > 0 && !collapsed && <span className="ps-group-label">{group.label}</span>}
        {group.items.map((item) => {
          const isActive = item.key === active;
          const openCards = sheetCounts[item.key] || 0;
          const badge = openCards > 0 ? openCards : item.badge;
          return (
            <button
              key={item.key}
              type="button"
              className={`ps-row${isActive ? " is-active" : ""}`}
              aria-current={isActive ? "page" : undefined}
              // The name is the only thing identifying a row once the label is
              // hidden, so it becomes the tooltip and the accessible name.
              title={collapsed ? item.name : undefined}
              aria-label={collapsed ? item.name : undefined}
              onClick={() => onSelect && onSelect(item.key)}
            >
              <span className="ps-row-icon">
                <Icon name={item.key} />
              </span>
              {!collapsed && <span className="ps-row-label">{item.name}</span>}
              {/* The rail keeps the count, as a small pill pinned to the icon's
                  top-right (1944:1850) — a different treatment AND a different
                  red from the expanded panel's disc. ZY-10350 drives this from
                  the live SheetCountsProvider so a section with open editor
                  cards surfaces "N" next to its name (and on the collapsed
                  rail's icon pill). */}
              {badge != null && (
                <span className={collapsed ? "ps-rail-badge" : "ps-badge"}>{badge}</span>
              )}
            </button>
          );
        })}
      </div>
    ))}
  </nav>
  );
};

PatientSidebar.propTypes = {
  /** key of the active section, e.g. "anthropometric". */
  active: PropTypes.string,
  onSelect: PropTypes.func,
  collapsed: PropTypes.bool,
  onToggle: PropTypes.func,
};

export default PatientSidebar;
